import React, { useState } from "react";
import axios from "axios";
import { useToast } from "../hooks/use-toast";
import { Send } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-md border border-neutral-300 bg-white px-4 py-2.5 text-sm text-neutral-900 outline-none transition-colors focus:border-[#F26522] focus:ring-2 focus:ring-[#F26522]/20";

const Field = ({ label, required, children }) => (
  <div>
    <label className="mb-1.5 block text-sm font-medium text-neutral-700">
      {label} {required && <span className="text-[#F26522]">*</span>}
    </label>
    {children}
  </div>
);

const ContactForm = () => {
  const { toast } = useToast();
  const { t, lang } = useLang();
  const L = t.contact.labels;
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: t.contact.errorTitle, description: t.contact.errorMsg, variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, lang });
      toast({ title: t.contact.successTitle, description: t.contact.successMsg });
      setForm({ name: "", email: "", message: "", website: "" });
    } catch (err) {
      const desc = err?.response?.status === 429 ? t.contact.rateLimit : t.contact.sendError;
      toast({ title: t.contact.errorTitle, description: desc, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Honeypot field - hidden from humans, bots tend to fill it */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
        <label>
          Website
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={update("website")}
          />
        </label>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label={L.name} required>
          <input type="text" value={form.name} onChange={update("name")} className={inputCls} placeholder={L.namePh} />
        </Field>
        <Field label={L.emailField} required>
          <input type="email" value={form.email} onChange={update("email")} className={inputCls} placeholder={L.emailPh} />
        </Field>
      </div>
      <Field label={L.message} required>
        <textarea value={form.message} onChange={update("message")} rows={5} className={inputCls + " resize-none"} placeholder={L.messagePh} />
      </Field>
      <button
        type="submit"
        disabled={submitting}
        className="group inline-flex items-center gap-2 rounded-md bg-[#F26522] px-7 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#d9561a] hover:shadow-lg disabled:opacity-70"
      >
        {submitting ? t.contact.submitting : t.contact.submit}
        <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
      </button>
    </form>
  );
};

export default ContactForm;
